import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Plus, Edit, Trash2, Loader2, AlertCircle, ReceiptIndianRupee } from "lucide-react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";

const emptyForm = {
    name: "",
    image: "",
    description: "",
    coins: "",
    status: "Active",
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "-");

export default function Gifts() {
    const [activeTab, setActiveTab] = useState("gifts");
    const [gifts, setGifts] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const formRef = useRef(null);
    const nameInputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [txPage, setTxPage] = useState(1);
    const [txLimit] = useState(20);
    const [txTotal, setTxTotal] = useState(0);
    const [txLoading, setTxLoading] = useState(false);

    // Multi-delete state
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);

    const txTotalPages = useMemo(() => Math.max(Math.ceil(txTotal / txLimit), 1), [txTotal, txLimit]);

    const fetchGifts = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const { data } = await API.get("/admin/gifts");
            if (!data?.success) throw new Error(data?.message || "Failed to load gifts");
            setGifts(Array.isArray(data.gifts) ? data.gifts : []);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Failed to load gifts");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchTransactions = useCallback(async (page = txPage) => {
        try {
            setTxLoading(true);
            setError("");
            const { data } = await API.get("/admin/gifts/transactions/list", {
                params: { page, limit: txLimit },
            });
            if (!data?.success) throw new Error(data?.message || "Failed to load gift transactions");
            setTransactions(Array.isArray(data.transactions) ? data.transactions : []);
            setTxTotal(Number(data.total || 0));
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Failed to load gift transactions");
        } finally {
            setTxLoading(false);
        }
    }, [txLimit, txPage]);

    useEffect(() => {
        fetchGifts();
    }, [fetchGifts]);

    useEffect(() => {
        if (activeTab === "transactions") {
            fetchTransactions(txPage);
        }
    }, [activeTab, txPage, fetchTransactions]);

    const handleToggleSelectAll = () => {
        if (selectedIds.length === gifts.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(gifts.map((g) => g._id));
        }
    };

    const handleToggleSelectRow = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleBatchDelete = async () => {
        try {
            setIsBatchDeleting(true);
            const { data } = await API.post("/admin/batch-delete", {
                entity: "gifts",
                ids: selectedIds,
            });
            if (data?.success) {
                setSelectedIds([]);
                fetchGifts();
            }
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Batch delete failed");
        } finally {
            setIsBatchDeleting(false);
        }
    };

    const resetForm = () => {
        setForm(emptyForm);
        setEditingId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSaving(true);
            setError("");

            const payload = {
                ...form,
                coins: Number(form.coins),
            };

            if (editingId) {
                await API.put(`/admin/gifts/${editingId}`, payload);
            } else {
                await API.post("/admin/gifts", payload);
            }

            resetForm();
            fetchGifts();
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (gift) => {
        setEditingId(gift._id);
        setActiveTab("gifts");
        setForm({
            name: gift.name || "",
            image: gift.image || "",
            description: gift.description || "",
            coins: String(gift.coins ?? ""),
            status: gift.status || "Active",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
            nameInputRef.current?.focus({ preventScroll: true });
        }, 150);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this gift?")) return;

        try {
            await API.delete(`/admin/gifts/${id}`);
            setGifts((prev) => prev.filter((g) => g._id !== id));
            setSelectedIds((prev) => prev.filter((i) => i !== id));
        } catch (err) {
            setError(err?.response?.data?.message || "Delete failed");
        }
    };

    return (
        <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Gift Items & Logs</h1>
                    <p className="text-sm text-gray-500">Manage virtual gifts and review sent gift transactions</p>
                </div>
            </div>

            <div className="flex items-center gap-2 border-b border-gray-200 dark:border-slate-700 pb-2">
                <button
                    onClick={() => setActiveTab("gifts")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "gifts"
                        ? "bg-pink-500 text-white"
                        : "bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                        }`}
                >
                    Gift Inventory ({gifts.length})
                </button>
                <button
                    onClick={() => setActiveTab("transactions")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "transactions"
                        ? "bg-pink-500 text-white"
                        : "bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                        }`}
                >
                    Gift Transactions ({txTotal})
                </button>
            </div>

            {error && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                </div>
            )}

            {activeTab === "gifts" && (
                <>
                    <form
                        ref={formRef}
                        onSubmit={handleSubmit}
                        className={`bg-white dark:bg-slate-800 rounded-xl border p-4 space-y-3 transition-all duration-300 scroll-mt-24 ${
                            editingId
                                ? "border-pink-500 ring-2 ring-pink-500/20 shadow-pink-500/10"
                                : "border-gray-200 dark:border-slate-700"
                        }`}
                    >
                        {editingId && (
                            <div className="flex items-center justify-between pb-2 border-b border-gray-100 dark:border-slate-700/60">
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
                                    <span className="text-sm font-semibold text-pink-600 dark:text-pink-400">
                                        Editing Gift: {form.name || "Untitled"}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline cursor-pointer"
                                >
                                    Cancel Edit
                                </button>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                                Gift Name
                                <input
                                    ref={nameInputRef}
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                                />
                            </label>

                            <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                                Image URL / Emoji
                                <input
                                    type="text"
                                    required
                                    value={form.image}
                                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                                />
                            </label>

                            <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                                Coin Price
                                <input
                                    type="number"
                                    min="1"
                                    required
                                    value={form.coins}
                                    onChange={(e) => setForm({ ...form, coins: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                                />
                            </label>

                            <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                                Status
                                <select
                                    value={form.status}
                                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                                >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                            </label>
                        </div>

                        <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200 block">
                            Description
                            <textarea
                                rows={2}
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                            />
                        </label>

                        <div className="flex items-center gap-3">
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-60 cursor-pointer"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                {editingId ? "Update Gift" : "Create Gift"}
                            </button>
                            {editingId && (
                                <button type="button" onClick={resetForm} className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer">
                                    Cancel edit
                                </button>
                            )}
                        </div>
                    </form>

                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-slate-700">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-slate-700/50">
                                        <th className="px-3 xl:px-4 py-3.5 xl:py-4 w-12 text-center">
                                            <input
                                                type="checkbox"
                                                checked={gifts.length > 0 && selectedIds.length === gifts.length}
                                                onChange={handleToggleSelectAll}
                                                className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                                            />
                                        </th>
                                        <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Gift</th>
                                        <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Coins</th>
                                        <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                        <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Created</th>
                                        <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={6} className="py-8 text-center text-gray-500">
                                                <Loader2 className="w-4 h-4 animate-spin inline" /> Loading...
                                            </td>
                                        </tr>
                                    ) : gifts.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="py-6 text-center text-gray-500">No gifts yet</td>
                                        </tr>
                                    ) : (
                                        gifts.map((gift) => {
                                            const isSelected = selectedIds.includes(gift._id);
                                            return (
                                            <tr key={gift._id} className={`transition-colors ${isSelected ? "bg-pink-50/70 dark:bg-pink-500/10" : "hover:bg-gray-50 dark:hover:bg-slate-700/30"}`}>
                                                <td className="px-3 xl:px-4 py-3.5 xl:py-4 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleToggleSelectRow(gift._id)}
                                                        className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                                                    />
                                                </td>
                                                <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-lg bg-pink-100 dark:bg-pink-500/10 flex items-center justify-center overflow-hidden">
                                                            {gift.image && gift.image.startsWith("http") ? (
                                                                <img src={gift.image} alt={gift.name} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <span className="text-lg">{gift.image || "🎁"}</span>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm text-gray-800 dark:text-gray-200 font-medium">{gift.name}</p>
                                                            <p className="text-xs text-gray-500 line-clamp-1">{gift.description || "No description"}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-cyan-600 font-semibold">{gift.coins}</td>
                                                <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${gift.status === "Active"
                                                        ? "bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400"
                                                        : "bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400"}`}>
                                                        {gift.status}
                                                    </span>
                                                </td>
                                                <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-gray-500">{formatDate(gift.createdAt)}</td>
                                                <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 space-x-2">
                                                    <button
                                                        onClick={() => handleEdit(gift)}
                                                        className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300"
                                                    >
                                                        <Edit className="w-3.5 h-3.5" /> Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(gift._id)}
                                                        className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-lg bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" /> Delete
                                                    </button>
                                                </td>
                                            </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Floating Multi-Delete Bar */}
                    <BatchActionBar
                        selectedCount={selectedIds.length}
                        totalCount={gifts.length}
                        onClearSelection={() => setSelectedIds([])}
                        onSelectAll={handleToggleSelectAll}
                        onDeleteSelected={handleBatchDelete}
                        isDeleting={isBatchDeleting}
                        entityName="gifts"
                    />
                </>
            )}

            {activeTab === "transactions" && (
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-slate-700">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 text-sm text-gray-600 dark:text-gray-300">
                        Gift coin transactions: {txTotal}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-slate-700/50">
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Users</th>
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Type</th>
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                                {txLoading ? (
                                    <tr>
                                        <td colSpan={5} className="py-8 text-center text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin inline" /> Loading...
                                        </td>
                                    </tr>
                                ) : transactions.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="py-6 text-center text-gray-500">No gift transactions found</td>
                                    </tr>
                                ) : (
                                    transactions.map((tx) => (
                                        <tr key={tx._id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-gray-700 dark:text-gray-200">
                                                <div className="flex flex-col">
                                                    <span>From: {tx?.fromUser?.name || "-"}</span>
                                                    <span className="text-xs text-gray-500">To: {tx?.toUser?.name || "-"}</span>
                                                </div>
                                            </td>
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-cyan-600 font-semibold inline-flex items-center gap-1">
                                                <ReceiptIndianRupee className="w-4 h-4" /> {tx.amount}
                                            </td>
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-gray-600 dark:text-gray-300">{tx.type || "gift_fee"}</td>
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                                <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400">
                                                    {tx.status || "success"}
                                                </span>
                                            </td>
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-gray-500">{formatDate(tx.createdAt)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border-t border-gray-200 dark:border-slate-700">
                        <p className="text-sm text-gray-600 dark:text-gray-400">Page {txPage} of {txTotalPages}</p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setTxPage((p) => Math.max(p - 1, 1))}
                                disabled={txPage === 1 || txLoading}
                                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setTxPage((p) => Math.min(p + 1, txTotalPages))}
                                disabled={txPage >= txTotalPages || txLoading}
                                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
